import React from 'react';
import { Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ReportIcon from '@mui/icons-material/Report';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
    
    // Here you could send error to your error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <div className="fintech-card p-8 text-center">
              {/* Error Icon */}
              <div className="w-20 h-20 bg-gradient-to-br from-danger-100 to-danger-200 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <ReportIcon className="text-danger-600 text-3xl" />
              </div>
              
              {/* Error Message */}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-gray-600 mb-6">
                We encountered an unexpected error. Don't worry, our team has been notified and is working to fix it.
              </p>
              
              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="text-left mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <summary className="cursor-pointer font-semibold text-gray-700 mb-2">
                    Error Details (Development)
                  </summary>
                  <div className="text-xs text-gray-600 space-y-2">
                    <div>
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                  className="w-full btn-primary"
                >
                  Refresh Page
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  className="w-full btn-secondary"
                >
                  Go to Dashboard
                </Button>
              </div>
              
              {/* Support Info */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">
                  Still having issues?
                </p>
                <div className="flex items-center justify-center space-x-4 text-sm">
                  <a 
                    href="mailto:support@gobella.com" 
                    className="text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
                  >
                    Contact Support
                  </a>
                  <span className="text-gray-300">•</span>
                  <a 
                    href="/help" 
                    className="text-primary-600 hover:text-primary-800 font-medium transition-colors duration-200"
                  >
                    Help Center
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export function withErrorBoundary(WrappedComponent, fallback = null) {
  return class extends ErrorBoundary {
    render() {
      if (this.state.hasError) {
        return fallback || (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-danger-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ReportIcon className="text-danger-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Component Error
            </h3>
            <p className="text-gray-600 mb-4">
              This component encountered an error and couldn't load properly.
            </p>
            <Button
              variant="outlined"
              size="small"
              onClick={this.handleRefresh}
              className="btn-secondary"
            >
              Try Again
            </Button>
          </div>
        );
      }
      return <WrappedComponent {...this.props} />;
    }
  };
}

export default ErrorBoundary; 